import { AppError } from "../errors/types";
import { reportAppError } from "../observability/crashReporter";
import { estimateStructureCostUsd, logMetric } from "../observability/metrics";
import { useChatStore } from "../state/chatStore";

// "correction" (暗黙訂正) / "undo" / "restart_current" は復唱確認フロー用の語彙だったが、
// auto-advance 一本化に伴う復唱確認の削除 (2026-07-18) で client から expected に渡す動線は
// 無くなった。server (functions/api/structure/openai.ts) は引き続き対応しており、API contract
// として型には残す (expected に含めない限り LLM プロンプトに影響しない)。
//   - goto_past: 「Q<N> に戻して」等 → 過去 Q 指定 (遡及編集 edit-mode で処理)
// spec: docs/superpowers/specs/2026-07-06-correction-ux-design.md §6.1 / §6.2
export type IntentLabel =
  | "submit"
  | "edit"
  | "resume"
  | "restart"
  | "yes"
  | "no"
  | "correction"
  | "undo"
  | "restart_current"
  | "goto_past";

// 氏名判定のラベル (spec 2026-07-25 D24)。server (functions/api/structure/openai.ts) にも
// 同じ union があるが、functions/ と src/ は別ビルドなので IntentLabel と同じく意図的に重複させる。
export const NAME_VERDICTS = ["name", "partial", "refusal", "unrelated"] as const;
export type NameVerdict = (typeof NAME_VERDICTS)[number];

// long / short の「回答として成立しているか」の判定ラベル (spec 2026-07-28 §3.3)。
// server (functions/api/structure/openai.ts) にも同じ union があるが、functions/ と src/ は
// 別ビルドなので NAME_VERDICTS と同じく意図的に重複させる。
export const ANSWER_VERDICTS = ["answer", "no-content", "loopback"] as const;
export type AnswerVerdictLabel = (typeof ANSWER_VERDICTS)[number];

export type StructureInput =
  | { questionType: "scale"; questionText: string; transcript: string; min: number; max: number }
  | { questionType: "short"; questionText: string; transcript: string }
  | { questionType: "long"; questionText: string; transcript: string }
  | {
      questionType: "intent";
      questionText: string;
      transcript: string;
      expected: IntentLabel[];
      // 直前 turn で AI が復唱した値 (scale=number, short/long=string)。
      // 与えられた場合、Server は system prompt に correction 判定ヒントを追加する。
      // 未指定なら従来の intent 判定 (backward-compatible)。
      previousValue?: number | string;
    };

type StructureResponse = {
  value: number | string | null;
  reason: string;
  intent?: IntentLabel | null;
  verdict?: string | null;
  durationMs?: number;
  usage?: { inputTokens?: number; outputTokens?: number; totalTokens?: number };
  model?: string;
};

export type StructureResult = {
  value: number | string | null;
  reason: string;
  intent: IntentLabel | null;
  /** short / long のみ。それ以外と、判定できなかった場合は null (= 判定しない)。 */
  verdict: AnswerVerdictLabel | null;
};

export async function structure(input: StructureInput): Promise<StructureResult> {
  const fetchOnce = () =>
    fetch("/api/structure/openai", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });

  const startedAt = performance.now();
  let res: Response;
  try {
    res = await fetchOnce();
    if (!res.ok && res.status >= 500) res = await fetchOnce();
  } catch (e) {
    // 回線断 / DNS 失敗 / 接続ハング。ここを囲まないと生の TypeError が
    // 呼び出し側へ飛び、AppError にすらならないので痕跡が 1 件も残らない
    // (spec 2026-07-29 §1.3(a))。最も知りたい失敗が最も残らない状態だった。
    reportAppError({
      code: "structure-failed",
      reason: "network",
      context: `structure:${input.questionType}`,
      message: e instanceof Error ? e.message : String(e),
    });
    throw e;
  }
  if (!res.ok) {
    reportAppError({
      code: "structure-failed",
      reason: "http",
      context: `structure:${input.questionType}`,
      message: `structure failed: ${res.status}`,
      httpStatus: res.status,
    });
    throw new AppError("structure-failed", `回答の整理に失敗しました (${res.status})`);
  }

  const json = (await res.json()) as StructureResponse;
  const roundTripMs = Math.round(performance.now() - startedAt);
  const inputTokens = json.usage?.inputTokens ?? 0;
  const outputTokens = json.usage?.outputTokens ?? 0;
  const totalTokens = json.usage?.totalTokens ?? inputTokens + outputTokens;

  // I3: aggregateSummary の cross-session 集計汚染防止のため sessionId を同梱する。
  const sessionId = useChatStore.getState().sessionId ?? undefined;
  logMetric({
    kind: "structure",
    provider: "openai",
    recordedAt: Date.now(),
    sessionId,
    questionType: input.questionType,
    serverDurationMs: json.durationMs ?? null,
    roundTripMs,
    inputTokens,
    outputTokens,
    totalTokens,
    model: json.model ?? "gpt-4o-mini",
    estimatedCostUsd: estimateStructureCostUsd(inputTokens, outputTokens),
  });

  // server 側でも未知値は落としているが、client でも塞ぐ (2 層防御 — server の
  // 実装が漏らしても ChatScreen に未知ラベルを渡さない。extractName と同じ作法)。
  const verdict = ANSWER_VERDICTS.includes(json.verdict as AnswerVerdictLabel)
    ? (json.verdict as AnswerVerdictLabel)
    : null;
  return { value: json.value, reason: json.reason, intent: json.intent ?? null, verdict };
}

// 氏名抽出 (spec 2026-07-18 D22)。structure() と同じ OpenAI proxy を questionType:"name" で
// 流用し、氏名 (漢字) と日本語ひらがな読みを返す。失敗は throw する (呼び出し側=NameCapture が
// 静的フォールバックに倒すトリガーにするため。非ブロッキングは呼び出し側で担保する)。
export async function extractName(
  transcript: string
): Promise<{ name: string; reading: string; verdict: NameVerdict | null }> {
  const startedAt = performance.now();
  let res: Response;
  try {
    res = await fetch("/api/structure/openai", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ questionType: "name", questionText: "", transcript }),
    });
  } catch (e) {
    reportAppError({
      code: "structure-failed",
      reason: "network",
      context: "structure:name",
      message: e instanceof Error ? e.message : String(e),
    });
    throw e;
  }
  if (!res.ok) {
    reportAppError({
      code: "structure-failed",
      reason: "http",
      context: "structure:name",
      message: `name extraction failed: ${res.status}`,
      httpStatus: res.status,
    });
    throw new AppError("structure-failed", `氏名の抽出に失敗しました (${res.status})`);
  }
  const json = (await res.json()) as {
    value?: string;
    reading?: string;
    verdict?: string;
    durationMs?: number;
    usage?: { inputTokens?: number; outputTokens?: number; totalTokens?: number };
    model?: string;
  };
  const inputTokens = json.usage?.inputTokens ?? 0;
  const outputTokens = json.usage?.outputTokens ?? 0;
  const sessionId = useChatStore.getState().sessionId ?? undefined;
  logMetric({
    kind: "structure",
    provider: "openai",
    recordedAt: Date.now(),
    sessionId,
    questionType: "name",
    serverDurationMs: json.durationMs ?? null,
    roundTripMs: Math.round(performance.now() - startedAt),
    inputTokens,
    outputTokens,
    totalTokens: json.usage?.totalTokens ?? inputTokens + outputTokens,
    model: json.model ?? "gpt-4o-mini",
    estimatedCostUsd: estimateStructureCostUsd(inputTokens, outputTokens),
  });
  // server 側でも未知値は落としているが、client でも塞ぐ (2 層防御 — server の
  // 実装が漏らしても NameCapture に未知ラベルを渡さない、D24-5)。
  const verdict = NAME_VERDICTS.includes(json.verdict as NameVerdict)
    ? (json.verdict as NameVerdict)
    : null;
  return { name: (json.value ?? "").trim(), reading: (json.reading ?? "").trim(), verdict };
}

// 氏名の順位付け (spec 2026-08-20 D4 / D6 / D21 / §3.3)。複数の音声認識エンジンが同じ発話を
// 書き起こした結果を渡し、確からしい読みを確からしい順に返させる。structure() と同じ
// OpenAI proxy を questionType:"name-rank" で流用する (鍵・json_object・temperature: 0・
// model・コスト計上・preview の smoke-test をすべて再利用できる)。
//
// **StructureInput の union には足さない** — StructureInput にはそもそも "name" が無く、
// extractName は body を直書きしている。こちらも同じ形 (body 直書き + 専用の戻り値型) に
// 揃える。触るのは StructureMetric.questionType の union だけである。
//
// **失敗は throw する** (extractName と同じ作法。呼び出し側 = NameCapture の
// startEnrichment が [] に倒す)。**5xx を自動リトライしない** — structure() の retry を
// 写すと課金と 3000ms の予算を二重に使う。
//
// **reportAppError は写さない (D21)。** app.error は CRITICAL_TYPES かつ 10 件/session で、
// rankNameCandidates と transcribeNameAudio がこれを踏むと MAX_RECOGNITIONS = 6 × 2 経路 =
// 最大 12 件が氏名 step だけで発生しうる。回線の悪いセッションでは**本編に入る前に上限を
// 使い切り**、以降の structure / tts / stt の app.error が黙って落ちる (上限に達したこと
// 自体は記録されないので気づけない)。失敗の観測点は preflight.name_ranked.outcome (D27)。
// server (functions/api/structure/openai.ts) にも同じ 2 つの定数があるが、functions/ と
// src/ は別ビルドなので NAME_VERDICTS / ANSWER_VERDICTS と同じく意図的に重複させる。
const RANK_MAX_RESULTS = 3;
const RANK_MAX_RESULT_LEN = 32;

/** @returns 確からしい順の読み (生の綴り、高々 3 件)。LLM が候補を出せなければ空配列 */
export async function rankNameCandidates(transcripts: string[]): Promise<string[]> {
  const startedAt = performance.now();
  const res = await fetch("/api/structure/openai", {
    method: "POST",
    headers: { "content-type": "application/json" },
    // **questionText / transcript は送らない** — name-rank は読まないので、使わないものを
    // 運ばない (§3.2 で上流の words を返さないのと同じ判断)。
    body: JSON.stringify({ questionType: "name-rank", transcripts }),
  });
  if (!res.ok) {
    throw new AppError("structure-failed", `氏名の順位付けに失敗しました (${res.status})`);
  }
  const json = (await res.json()) as {
    ranked?: unknown;
    durationMs?: number;
    usage?: { inputTokens?: number; outputTokens?: number; totalTokens?: number };
    model?: string;
  };
  const inputTokens = json.usage?.inputTokens ?? 0;
  const outputTokens = json.usage?.outputTokens ?? 0;
  const sessionId = useChatStore.getState().sessionId ?? undefined;
  logMetric({
    kind: "structure",
    provider: "openai",
    recordedAt: Date.now(),
    sessionId,
    questionType: "name-rank",
    serverDurationMs: json.durationMs ?? null,
    roundTripMs: Math.round(performance.now() - startedAt),
    inputTokens,
    outputTokens,
    totalTokens: json.usage?.totalTokens ?? inputTokens + outputTokens,
    model: json.model ?? "gpt-4o-mini",
    estimatedCostUsd: estimateStructureCostUsd(inputTokens, outputTokens),
  });
  // server 側でも同じ検証を通しているが、client でも塞ぐ (2 層防御 — server の実装が
  // 漏らしても NameCapture に変なものを渡さない。extractName の verdict と同じ作法)。
  //
  // **canon 化してはいけない。** 実測スクリプトの clean_ranked は重複判定のために
  // canon() (= 本番の matchKey) を通して**canon 形を返している**。本番がこれを写すと
  // 画面と displayName が canon 空間の綴り (カトーシオリ / キクチリョーマ) になり、
  // D7 が 0 件にしたはずの症状を自分で作る。**残すのは生の綴り**で、重複は
  // buildNameCandidates が matchKey で見る。
  if (!Array.isArray(json.ranked)) return [];
  return json.ranked
    .filter((r): r is string => typeof r === "string")
    .map((r) => r.trim().slice(0, RANK_MAX_RESULT_LEN))
    .filter((r) => r.length > 0)
    .slice(0, RANK_MAX_RESULTS);
}
